import React, { useState } from 'react';
import { ArrowLeft, LayoutGrid, Boxes, AlertCircle, Lightbulb, Settings, FileText } from 'lucide-react';
import MaturityGauge from '../components/MaturityGauge';
import PillarOverviewTab from './PillarOverviewTab';
import PillarTechnologiesTab from './PillarTechnologiesTab';
import PillarGapsTab from './PillarGapsTab';
import PillarRecommendationsTab from './PillarRecommendationsTab';
import PillarConfigurationsTab from './PillarConfigurationsTab';
import PillarEvidenceTab from './PillarEvidenceTab';
import { CISA_PILLARS, CISA_FUNCTIONS } from '../../config/cisaMaturityModel';
import './CisaPillarAssessment.css';

/**
 * CisaPillarAssessment - Container for pillar deep-dive with 6 subtabs
 *
 * Props:
 * - pillarCode: string (e.g., 'IDENTITY')
 * - assessments: { [functionCode]: maturityLevel }
 * - targetLevels: { [functionCode]: maturityLevel }
 * - productAdoptions: { [functionCode]: [{ productName, status }] }
 * - configCompletions: { [functionCode]: { completedSteps: number[], totalSteps: number } }
 * - summary: { pillarScores, ... }
 * - onBack: () => void
 * - onUpdateLevel: (functionCode, level) => void
 * - onUpdateTargetLevel: (functionCode, level) => void
 * - onAdoptProduct: (functionCode, productName, status) => void
 * - onUpdateProductStatus: (functionCode, productName, status) => void
 * - onRemoveProduct: (functionCode, productName) => void
 * - onToggleConfigStep: (functionCode, stepIndex) => void
 */
const CisaPillarAssessment = ({
  pillarCode,
  assessments = {},
  targetLevels = {},
  productAdoptions = {},
  configCompletions = {},
  summary = {},
  onBack,
  onUpdateLevel,
  onUpdateTargetLevel,
  onAdoptProduct,
  onUpdateProductStatus,
  onRemoveProduct,
  onToggleConfigStep
}) => {
  const [activeTab, setActiveTab] = useState('overview');

  // Get pillar info
  const pillar = CISA_PILLARS.find(p => p.code === pillarCode);
  const functions = CISA_FUNCTIONS[pillarCode] || [];
  const pillarScore = summary.pillarScores?.[pillarCode] || 1.0;

  // Tab configuration
  const tabs = [
    { id: 'overview', label: 'Overview', icon: LayoutGrid },
    { id: 'technologies', label: 'Technologies', icon: Boxes },
    { id: 'gaps', label: 'Gaps', icon: AlertCircle },
    { id: 'recommendations', label: 'Recommendations', icon: Lightbulb },
    { id: 'configurations', label: 'Configurations', icon: Settings },
    { id: 'evidence', label: 'Evidence', icon: FileText }
  ];

  // Render active tab content
  const renderTabContent = () => {
    const commonProps = {
      pillarCode,
      pillar,
      functions,
      assessments,
      targetLevels,
      productAdoptions,
      configCompletions,
      summary
    };

    switch (activeTab) {
      case 'overview':
        return (
          <PillarOverviewTab
            {...commonProps}
            onUpdateLevel={onUpdateLevel}
            onUpdateTargetLevel={onUpdateTargetLevel}
          />
        );
      case 'technologies':
        return (
          <PillarTechnologiesTab
            {...commonProps}
            onAdoptProduct={onAdoptProduct}
            onUpdateProductStatus={onUpdateProductStatus}
            onRemoveProduct={onRemoveProduct}
          />
        );
      case 'gaps':
        return (
          <PillarGapsTab
            {...commonProps}
            onUpdateTargetLevel={onUpdateTargetLevel}
          />
        );
      case 'recommendations':
        return (
          <PillarRecommendationsTab
            {...commonProps}
            onAdoptProduct={onAdoptProduct}
          />
        );
      case 'configurations':
        return (
          <PillarConfigurationsTab
            {...commonProps}
            onToggleConfigStep={onToggleConfigStep}
          />
        );
      case 'evidence':
        return <PillarEvidenceTab {...commonProps} />;
      default:
        return null;
    }
  };

  if (!pillar) {
    return (
      <div className="cisa-pillar-assessment-error">
        <AlertCircle size={24} />
        <p>Pillar not found: {pillarCode}</p>
        <button type="button" onClick={onBack}>Go Back</button>
      </div>
    );
  }

  return (
    <div className="cisa-pillar-assessment" style={{ '--pillar-color': pillar.color }}>
      {/* Header */}
      <div className="cisa-pillar-assessment-header">
        <button type="button" className="cisa-pillar-assessment-back" onClick={onBack}>
          <ArrowLeft size={18} />
          <span>Back</span>
        </button>

        <div className="cisa-pillar-assessment-title">
          <div
            className="cisa-pillar-assessment-icon"
            style={{ background: pillar.color }}
          >
            {pillar.name.charAt(0)}
          </div>
          <div className="cisa-pillar-assessment-title-text">
            <h2>{pillar.name} Pillar Assessment</h2>
            <span className="cisa-pillar-assessment-subtitle">
              {functions.length} functions to assess
            </span>
          </div>
        </div>

        <div className="cisa-pillar-assessment-score">
          <MaturityGauge score={pillarScore} size="small" showLabel={false} />
          <span className="cisa-pillar-assessment-score-label">
            Score: {pillarScore.toFixed(1)}
          </span>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="cisa-pillar-assessment-tabs">
        {tabs.map(tab => {
          const IconComponent = tab.icon;
          const isActive = activeTab === tab.id;

          return (
            <button
              key={tab.id}
              type="button"
              className={`cisa-pillar-assessment-tab ${isActive ? 'cisa-pillar-assessment-tab--active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <IconComponent size={16} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div className="cisa-pillar-assessment-content">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default CisaPillarAssessment;
